// src/pages/Admin/Business/Finance/financeCommitments.utils.js

export function safeNumber(value) {
  if (value === null || value === undefined || value === "") return 0;

  const normalized =
    typeof value === "string" ? value.replace(",", ".").trim() : value;

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

export function parseISODate(dateStr) {
  if (!dateStr) return null;

  const raw = String(dateStr).trim();

  if (
    raw === "0000-00-00" ||
    raw === "0000-00-00 00:00:00" ||
    raw.startsWith("0000-00-00")
  ) {
    return null;
  }

  const onlyDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (onlyDate) {
    const year = Number(onlyDate[1]);
    const month = Number(onlyDate[2]);
    const day = Number(onlyDate[3]);

    if (!year || !month || !day) return null;

    const d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

export function formatDate(dateValue) {
  const d = dateValue instanceof Date ? dateValue : parseISODate(dateValue);
  if (!d) return "—";

  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function toInputDate(dateValue) {
  const d = dateValue instanceof Date ? dateValue : parseISODate(dateValue);
  if (!d) return "";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

export function fmtMoney(value, withDecimals = false) {
  return safeNumber(value).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: withDecimals ? 2 : 0,
    maximumFractionDigits: withDecimals ? 2 : 0,
  });
}

export function monthKey(dateValue) {
  const d = dateValue instanceof Date ? dateValue : parseISODate(dateValue);
  if (!d) return "";

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function monthLabel(dateValue) {
  const d = dateValue instanceof Date ? dateValue : parseISODate(dateValue);
  if (!d) return "";

  return d.toLocaleDateString("fr-FR", {
    month: "short",
    year: "2-digit",
  });
}

export function slugify(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function getAssociationId(item) {
  return item?.asso_id || item?.association?.id || "";
}

function getAssociationName(item) {
  return (
    item?.association?.nom_asso ||
    item?.Association?.nom_asso ||
    item?.nom_asso ||
    "Association inconnue"
  );
}

function getAssociationLogo(item) {
  return item?.association?.logo_asso || item?.Association?.logo_asso || "";
}

function exactExpenseKey(expense) {
  return [
    getAssociationId(expense) || "0",
    slugify(expense.categorie_depense),
    slugify(expense.nom_depense),
  ].join("__");
}

function exactDeadlineKey(deadline) {
  return [
    getAssociationId(deadline) || "0",
    slugify(deadline.categorie_echeance),
    slugify(deadline.nom_echeance),
  ].join("__");
}

function categoryKeyFromExpense(expense) {
  return [
    getAssociationId(expense) || "0",
    slugify(expense.categorie_depense),
  ].join("__");
}

function categoryKeyFromDeadline(deadline) {
  return [
    getAssociationId(deadline) || "0",
    slugify(deadline.categorie_echeance),
  ].join("__");
}

function pickLatestExpense(current, candidate) {
  if (!current) return candidate;

  const currentDate = parseISODate(current.date_depense)?.getTime() || 0;
  const candidateDate = parseISODate(candidate.date_depense)?.getTime() || 0;

  return candidateDate > currentDate ? candidate : current;
}

export function getCommitmentMeta(item) {
  const baseDate =
    item.entityType === "echeance"
      ? item.endDate || item.startDate || item.dueDate
      : item.dueDate || item.startDate || item.endDate;

  const due = parseISODate(baseDate);

  if (!due) {
    return {
      tone: "gray",
      label: "Sans date",
      days: null,
    };
  }

  const today = startOfDay(new Date());
  const target = startOfDay(due);
  const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));

  if (diff < 0) {
    if (item.entityType === "depense") {
      return {
        tone: "gray",
        label: "Passée",
        days: diff,
      };
    }

    return {
      tone: "red",
      label: `En retard · ${Math.abs(diff)} j`,
      days: diff,
    };
  }

  if (diff === 0) {
    return {
      tone: "blue",
      label: "Aujourd’hui",
      days: diff,
    };
  }

  if (diff <= 7) {
    return {
      tone: "red",
      label: `Urgent · ${diff} j`,
      days: diff,
    };
  }

  if (diff <= 30) {
    return {
      tone: "orange",
      label: "< 30 j",
      days: diff,
    };
  }

  if (diff <= 60) {
    return {
      tone: "yellow",
      label: "< 60 j",
      days: diff,
    };
  }

  return {
    tone: "green",
    label: `${diff} j`,
    days: diff,
  };
}

function getComparableDueTime(item) {
  const date =
    item.entityType === "echeance"
      ? item.endDate || item.startDate || item.dueDate
      : item.dueDate || item.startDate || item.endDate;

  return parseISODate(date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
}

export function sortCommitments(a, b, sortBy) {
  const dueA = getComparableDueTime(a);
  const dueB = getComparableDueTime(b);

  const amountA = safeNumber(a.displayAmount);
  const amountB = safeNumber(b.displayAmount);

  const assoA = String(a.associationName || "").toLowerCase();
  const assoB = String(b.associationName || "").toLowerCase();

  const titleA = String(a.title || "").toLowerCase();
  const titleB = String(b.title || "").toLowerCase();

  switch (sortBy) {
    case "due_desc":
      return dueB - dueA;

    case "amount_desc":
      return amountB - amountA;

    case "amount_asc":
      return amountA - amountB;

    case "association_asc":
      return assoA.localeCompare(assoB, "fr");

    case "title_asc":
      return titleA.localeCompare(titleB, "fr");

    case "due_asc":
    default:
      return dueA - dueB;
  }
}

export function buildCommitments(expenses = [], deadlines = []) {
  const latestExpenseByExactKey = new Map();
  const latestExpenseByCategoryKey = new Map();

  expenses.forEach((expense) => {
    const exactKey = exactExpenseKey(expense);
    const catKey = categoryKeyFromExpense(expense);

    latestExpenseByExactKey.set(
      exactKey,
      pickLatestExpense(latestExpenseByExactKey.get(exactKey), expense)
    );

    latestExpenseByCategoryKey.set(
      catKey,
      pickLatestExpense(latestExpenseByCategoryKey.get(catKey), expense)
    );
  });

  const normalizedExpenses = expenses.map((expense) => {
    const amount = safeNumber(expense.montant_depense);
    const date = expense.date_depense || null;

    return {
      uid: `depense-${expense.id}`,
      id: expense.id,
      entityType: "depense",
      raw: expense,

      title: expense.nom_depense || "Dépense",
      category: expense.categorie_depense || "Non classée",
      counterparty: expense.destinataire_depense || "—",

      associationId: getAssociationId(expense),
      associationName: getAssociationName(expense),
      associationLogo: getAssociationLogo(expense),

      startDate: date,
      endDate: date,
      dueDate: date,

      amount,
      estimatedAmount: amount,
      displayAmount: amount,

      autoRenew: false,
      invoiceLink: expense.facture_depense || "",
      originLabel: "Dépense réelle",
      matchedExpenseDate: date,
    };
  });

  const normalizedDeadlines = deadlines.map((deadline) => {
    const exactKey = exactDeadlineKey(deadline);
    const catKey = categoryKeyFromDeadline(deadline);

    const matchedExpense =
      latestExpenseByExactKey.get(exactKey) ||
      latestExpenseByCategoryKey.get(catKey) ||
      null;

    const estimatedAmount = safeNumber(
      deadline.montant_estime_echeance ??
        deadline.montant_echeance ??
        matchedExpense?.montant_depense
    );

    const startDate = deadline.debut_echeance || null;
    const endDate = deadline.fin_echeance || null;

    return {
      uid: `echeance-${deadline.id}`,
      id: deadline.id,
      entityType: "echeance",
      raw: deadline,

      title: deadline.nom_echeance || "Échéance",
      category: deadline.categorie_echeance || "Non classée",
      counterparty: matchedExpense?.destinataire_depense || "—",

      associationId: getAssociationId(deadline),
      associationName: getAssociationName(deadline),
      associationLogo: getAssociationLogo(deadline),

      startDate,
      endDate,
      dueDate: endDate || startDate,

      amount: 0,
      estimatedAmount,
      displayAmount: estimatedAmount,

      autoRenew: Boolean(deadline.renouvellement_auto),
      invoiceLink: matchedExpense?.facture_depense || "",
      originLabel: matchedExpense
        ? "Estimée depuis la dernière dépense"
        : "Sans historique",
      matchedExpenseDate: matchedExpense?.date_depense || null,
    };
  });

  return [...normalizedExpenses, ...normalizedDeadlines];
}

export function buildUpcomingRows(items = [], horizon = 6) {
  const today = startOfDay(new Date());
  const rows = [];

  for (let i = 0; i < horizon; i += 1) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);

    rows.push({
      key: monthKey(d),
      label: monthLabel(d),
      count: 0,
      amount: 0,
    });
  }

  const index = new Map(rows.map((row, idx) => [row.key, idx]));

  items.forEach((item) => {
    const due = parseISODate(
      item.entityType === "echeance"
        ? item.endDate || item.startDate || item.dueDate
        : item.dueDate || item.startDate
    );

    if (!due) return;

    const meta = getCommitmentMeta(item);
    if (meta.days !== null && meta.days < 0) return;

    const key = monthKey(due);
    const rowIndex = index.get(key);

    if (rowIndex === undefined) return;

    rows[rowIndex].count += 1;
    rows[rowIndex].amount += safeNumber(item.displayAmount);
  });

  return rows;
}

export function buildStatusRows(items = []) {
  const initial = {
    urgent: 0,
    soon: 0,
    medium: 0,
    future: 0,
    late: 0,
    nodate: 0,
  };

  items.forEach((item) => {
    const meta = getCommitmentMeta(item);

    if (meta.days === null) {
      initial.nodate += 1;
      return;
    }

    if (meta.days < 0) {
      initial.late += 1;
      return;
    }

    if (meta.days <= 7) {
      initial.urgent += 1;
      return;
    }

    if (meta.days <= 30) {
      initial.soon += 1;
      return;
    }

    if (meta.days <= 60) {
      initial.medium += 1;
      return;
    }

    initial.future += 1;
  });

  return [
    { name: "Urgent", value: initial.urgent, color: "#ef4444" },
    { name: "< 30 j", value: initial.soon, color: "#f97316" },
    { name: "< 60 j", value: initial.medium, color: "#facc15" },
    { name: "Plus tard", value: initial.future, color: "#22c55e" },
    { name: "En retard", value: initial.late, color: "#8b5cf6" },
    { name: "Sans date", value: initial.nodate, color: "#94a3b8" },
  ].filter((row) => row.value > 0);
}